<template>
    <b-overlay
            :show="processing"
            rounded="sm"
    >
        <template v-slot:overlay>
            <div class="text-center p-4 bg-primary text-light rounded">
                <b-icon
                        icon="cloud-download"
                        font-scale="4"
                />
                <div class="mb-1 mt-2">
                    در حال آماده‌سازی فایل ...
                </div>
            </div>
        </template>

        <b-card title="مدیریت سفارش‌ها">

            <!-- فیلترها -->
            <div class="custom-search">
                <b-row>
                    <b-col md="3">
                        <b-form-group
                                label="جستجو"
                                label-for="Search"
                        >
                            <b-form-input
                                    id="Search"
                                    v-model="filterOptions.Search"
                                    placeholder="کد سفارش / موبایل / ایمیل"
                                    @keyup.enter="update(1)"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="2">
                        <b-form-group label="وضعیت">
                            <v-select
                                    v-model="filterOptions.Status"
                                    :options="enums('OrderStatus')"
                                    dir="rtl"
                                    label="text"
                                    :reduce="x => x.value"
                                    placeholder="همه"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="2">
                        <b-form-group label="پرداخت">
                            <v-select
                                    v-model="filterOptions.IsPaid"
                                    :options="enums('IsPaid')"
                                    dir="rtl"
                                    label="text"
                                    :reduce="x => x.value"
                                    placeholder="همه"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="2">
                        <b-form-group label="منبع">
                            <v-select
                                    v-model="filterOptions.Source"
                                    :options="enums('FlightSource')"
                                    dir="rtl"
                                    label="text"
                                    :reduce="x => x.value"
                                    placeholder="همه"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="3" />
                    <b-col md="2">
                        <b-form-group
                                label="از تاریخ"
                                label-for="FromDate"
                        >
                            <date-picker
                                    v-model="filterOptions.FromDate"
                                    placeholder=""
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="2">
                        <b-form-group
                                label="تا تاریخ"
                                label-for="ToDate"
                        >
                            <date-picker
                                    v-model="filterOptions.ToDate"
                                    placeholder=""
                            />
                        </b-form-group>
                    </b-col>
                    <b-col
                            md="8"
                            class="d-flex align-items-end justify-content-end mb-1"
                    >
                        <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="mr-1"
                                variant="warning"
                                @click="update(1)"
                        >فیلتر
                        </b-button>
                        <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                variant="outline-success"
                                @click="exportExcel"
                        >خروجی اکسل
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <!-- جدول سفارش‌ها -->
            <b-table
                    bordered
                    hover
                    striped
                    responsive
                    class="mt-1 mb-2 position-relative"
                    :items="items"
                    :fields="fields"
                    :no-local-sorting="true"
                    :sort-by="filterOptions.SortBy"
                    :sort-desc="filterOptions.IsDesc"
                    :busy="loading"
                    @sort-changed="sortChange"
            >
                <template #cell(Id)="data">
                    <b-link
                            class="font-weight-bold"
                            @click="openDetails(data.item.Id)"
                    >
                        #{{ data.value }}
                    </b-link>
                </template>

                <template #cell(User)="data">
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold text-nowrap">
                            {{ data.item.UserName ? data.item.UserName : 'مهمان' }}
                        </span>
                        <small class="text-muted" dir="ltr">
                            {{ data.item.ContactMobile || data.item.UserMobile || '—' }}
                        </small>
                    </div>
                </template>

                <template #cell(Route)="data">
                    <div class="d-flex align-items-center">
                        <span>{{ data.item.OriginTitle || '—' }}</span>
                        <feather-icon
                                :icon="data.item.IsRoundTrip ? 'RepeatIcon' : 'ArrowLeftIcon'"
                                size="14"
                                class="mx-50 text-primary"
                        />
                        <span>{{ data.item.DestinationTitle || '—' }}</span>
                        <b-badge
                                v-if="data.item.Source !== null"
                                class="ml-1"
                                :variant="'light-' + enums('FlightSource', 'value', data.item.Source, 'badge')"
                        >
                            {{ data.item.SourceTitle }}
                        </b-badge>
                    </div>
                </template>

                <template #cell(PassengerCount)="data">
                    <b-badge variant="light-secondary">{{ data.value }} نفر</b-badge>
                </template>

                <template #cell(PayPrice)="data">
                    {{ data.value | price }}
                </template>

                <template #cell(IsPaid)="data">
                    <div class="d-flex flex-column">
                        <b-badge :variant="data.value ? 'light-success' : 'light-warning'">
                            {{ data.value ? 'پرداخت شده' : 'پرداخت نشده' }}
                        </b-badge>
                        <small v-if="data.item.BankTitle" class="text-muted mt-25">{{ data.item.BankTitle }}</small>
                    </div>
                </template>

                <template #cell(Status)="data">
                    <b-badge
                            v-if="data.value !== null"
                            style="cursor: pointer;"
                            :variant="'light-' + enums('OrderStatus', 'value', data.value, 'badge')"
                            @click="openStatus(data.item)"
                    >
                        {{ enums('OrderStatus', 'value', data.value, 'text') }}
                        <feather-icon icon="Edit2Icon" size="11" />
                    </b-badge>
                    <feather-icon
                            v-if="data.item.HasIssueError"
                            v-b-tooltip.hover
                            title="خطا در صدور بلیط"
                            icon="AlertTriangleIcon"
                            size="16"
                            class="text-danger ml-50"
                    />
                </template>

                <template #cell(CreatedAt)="data">
                    <span class="text-nowrap">{{ data.value | date }}</span>
                </template>

                <template #cell(actions)="data">
                    <div class="d-flex">
                        <feather-icon
                                v-b-tooltip.hover
                                title="جزئیات"
                                icon="EyeIcon"
                                size="18"
                                class="cursor-pointer text-primary mr-1"
                                @click="openDetails(data.item.Id)"
                        />
                        <feather-icon
                                v-if="data.item.IsPaid && data.item.Source === 0"
                                v-b-tooltip.hover
                                title="دانلود بلیط"
                                icon="DownloadIcon"
                                size="18"
                                class="cursor-pointer text-success"
                                @click="downloadTicket(data.item.Id)"
                        />
                    </div>
                </template>

                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner variant="primary" class="align-middle" />
                        <strong class="ml-1">در حال دریافت اطلاعات ...</strong>
                    </div>
                </template>
            </b-table>

            <z-pagination
                    :page="filterOptions.Page"
                    :total-rows="totalRows"
                    :loading="loading"
                    @change="update"
            />
        </b-card>

        <!-- مودال جزئیات سفارش -->
        <b-modal
                v-model="detailsModal"
                size="xl"
                hide-footer
                centered
                :title="detail ? ('جزئیات سفارش #' + detail.Id) : 'جزئیات سفارش'"
        >
            <div v-if="detailLoading" class="text-center my-3">
                <b-spinner variant="primary" />
            </div>

            <div v-else-if="detail">
                <!-- هشدار خطای صدور -->
                <b-alert
                        v-if="detail.HasIssueError"
                        variant="danger"
                        show
                        class="mb-1"
                >
                    <div class="alert-body">
                        <feather-icon icon="AlertTriangleIcon" class="mr-50" />
                        <strong>خطای صدور بلیط: </strong>{{ detail.IssueError }}
                    </div>
                </b-alert>

                <!-- خلاصه -->
                <b-row class="mb-1">
                    <b-col md="8">
                        <b-card no-body class="border p-1 mb-0 h-100">
                            <h6 class="mb-1">اطلاعات سفارش</h6>
                            <b-row>
                                <b-col md="6" class="mb-50">
                                    <span class="text-muted">وضعیت: </span>
                                    <b-badge :variant="'light-' + enums('OrderStatus', 'value', detail.Status, 'badge')">
                                        {{ enums('OrderStatus', 'value', detail.Status, 'text') }}
                                    </b-badge>
                                </b-col>
                                <b-col md="6" class="mb-50">
                                    <span class="text-muted">تاریخ ثبت: </span>{{ detail.CreatedAt | date }}
                                </b-col>
                                <b-col md="6" class="mb-50">
                                    <span class="text-muted">مبلغ قابل پرداخت: </span>
                                    <strong>{{ detail.PayPrice | price }}</strong>
                                </b-col>
                                <b-col md="6" class="mb-50">
                                    <span class="text-muted">تخفیف: </span>{{ detail.Discount | price }}
                                </b-col>
                                <b-col md="6" class="mb-50">
                                    <span class="text-muted">موبایل تماس: </span>
                                    <span dir="ltr">{{ detail.ContactMobile || '—' }}</span>
                                </b-col>
                                <b-col md="6" class="mb-50">
                                    <span class="text-muted">ایمیل تماس: </span>
                                    <span dir="ltr">{{ detail.ContactEmail || '—' }}</span>
                                </b-col>
                            </b-row>
                        </b-card>
                    </b-col>
                    <b-col md="4">
                        <b-card no-body class="border p-1 mb-0 h-100">
                            <h6 class="mb-1">کاربر</h6>
                            <template v-if="detail.User">
                                <div class="font-weight-bold">{{ detail.User.Name }} {{ detail.User.Family }}</div>
                                <div class="text-muted" dir="ltr">{{ detail.User.Mobile }}</div>
                                <div class="text-muted" dir="ltr">{{ detail.User.Email }}</div>
                            </template>
                            <span v-else class="text-muted">کاربر مهمان</span>
                        </b-card>
                    </b-col>
                </b-row>

                <!-- پروازها -->
                <h6 class="mt-1 mb-1">پروازها</h6>
                <b-card
                        v-for="(f, i) in detail.Flights"
                        :key="'f' + i"
                        no-body
                        class="border p-1 mb-1"
                >
                    <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap">
                        <div class="d-flex align-items-center">
                            <b-avatar
                                    v-if="f.AirlineImage"
                                    :src="f.AirlineImage"
                                    square
                                    size="34"
                                    class="mr-1"
                            />
                            <div>
                                <span class="font-weight-bold">
                                    {{ f.AirlineTitle || f.AirlineIata || '—' }}
                                </span>
                                <b-badge class="ml-50" :variant="'light-' + enums('FlightSource', 'value', f.Source, 'badge')">
                                    {{ f.SourceTitle }}
                                </b-badge>
                                <b-badge v-if="f.IsReturning" variant="light-info" class="ml-50">برگشت</b-badge>
                            </div>
                        </div>
                        <div class="text-muted">
                            شماره پرواز: <span dir="ltr">{{ f.Number || '—' }}</span>
                        </div>
                    </div>

                    <b-row>
                        <b-col md="4" class="mb-50">
                            <span class="text-muted">مسیر: </span>
                            {{ f.OriginCity || f.OriginIata || '—' }}
                            ←
                            {{ f.DestinationCity || f.DestinationIata || '—' }}
                        </b-col>
                        <b-col md="3" class="mb-50">
                            <span class="text-muted">تاریخ: </span>{{ f.Date | date }}
                        </b-col>
                        <b-col md="2" class="mb-50">
                            <span class="text-muted">حرکت: </span>{{ time(f.DepartureTime) }}
                        </b-col>
                        <b-col md="3" class="mb-50">
                            <span class="text-muted">کلاس: </span>{{ f.ClassTitle || f.Cabin || '—' }}
                        </b-col>
                        <b-col md="4" class="mb-50">
                            <span class="text-muted">قیمت: </span>{{ f.Price | price }}
                        </b-col>
                        <b-col v-if="f.SupplierPnr || f.LocalPnr" md="4" class="mb-50">
                            <span class="text-muted">PNR: </span>
                            <span dir="ltr">{{ f.SupplierPnr || f.LocalPnr }}</span>
                        </b-col>
                        <b-col v-if="f.TicketNumber" md="4" class="mb-50">
                            <span class="text-muted">شماره بلیط: </span>
                            <span dir="ltr">{{ f.TicketNumber }}</span>
                        </b-col>
                    </b-row>
                </b-card>

                <!-- مسافران -->
                <h6 class="mt-1 mb-1">مسافران</h6>
                <b-table
                        bordered
                        small
                        responsive
                        :items="detail.Passengers"
                        :fields="passengerFields"
                        class="mb-1"
                >
                    <template #cell(DateOfBirth)="data">
                        {{ data.value | date }}
                    </template>
                    <template #cell(Fare)="data">
                        {{ data.value | price }}
                    </template>
                </b-table>

                <!-- پرداخت‌ها -->
                <h6 class="mt-1 mb-1">پرداخت‌ها</h6>
                <b-table
                        v-if="detail.Payments && detail.Payments.length"
                        bordered
                        small
                        responsive
                        :items="detail.Payments"
                        :fields="paymentFields"
                        class="mb-1"
                >
                    <template #cell(IsPaid)="data">
                        <b-badge :variant="data.value ? 'light-success' : 'light-warning'">
                            {{ data.value ? 'موفق' : 'ناموفق' }}
                        </b-badge>
                    </template>
                    <template #cell(CreatedAt)="data">
                        {{ data.value | date }}
                    </template>
                </b-table>
                <p v-else class="text-muted">پرداختی ثبت نشده است.</p>

                <div class="text-left mt-1">
                    <b-button
                            v-if="detail.IsPaid"
                            variant="success"
                            @click="downloadTicket(detail.Id)"
                    >
                        <feather-icon icon="DownloadIcon" class="mr-50" />
                        دانلود بلیط داخلی
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- مودال تغییر وضعیت -->
        <b-modal
                v-model="statusModal"
                title="تغییر وضعیت سفارش"
                ok-title="ذخیره"
                cancel-title="انصراف"
                cancel-variant="outline-secondary"
                centered
                size="sm"
                :no-close-on-backdrop="true"
                @ok="saveStatus"
        >
            <b-form-group label="وضعیت جدید">
                <v-select
                        v-model="statusRow.Status"
                        :options="enums('OrderStatus')"
                        dir="rtl"
                        label="text"
                        :reduce="x => x.value"
                        append-to-body
                />
            </b-form-group>
        </b-modal>
    </b-overlay>
</template>

<script>
import { BIcon, BIconCloudDownload } from 'bootstrap-vue'

export default {
    name: 'Order',
    components: {
        BIcon,
        BIconCloudDownload,
    },
    data() {
        return {
            totalRows: 1000 * 1000,
            loading: false,
            processing: false,
            filterOptions: {
                Search: null,
                Status: null,
                IsPaid: null,
                Source: null,
                FromDate: null,
                ToDate: null,
                Page: 1,
                SortBy: 'Id',
                IsDesc: true,
            },
            fields: [
                { key: 'Id', label: '#', sortable: true },
                { key: 'User', label: 'کاربر / تماس' },
                { key: 'Route', label: 'مسیر' },
                { key: 'PassengerCount', label: 'مسافر' },
                { key: 'PayPrice', label: 'مبلغ', sortable: true },
                { key: 'IsPaid', label: 'پرداخت' },
                { key: 'Status', label: 'وضعیت' },
                { key: 'CreatedAt', label: 'تاریخ', sortable: true },
                { key: 'actions', label: '' },
            ],
            passengerFields: [
                { key: 'Name', label: 'نام' },
                { key: 'Family', label: 'خانوادگی' },
                { key: 'TypeTitle', label: 'نوع' },
                { key: 'GenderTitle', label: 'جنسیت' },
                { key: 'NationalCode', label: 'کد ملی' },
                { key: 'PassportNumber', label: 'پاسپورت' },
                { key: 'Nationality', label: 'ملیت' },
                { key: 'Fare', label: 'کرایه' },
            ],
            paymentFields: [
                { key: 'IsPaid', label: 'وضعیت' },
                { key: 'BankTitle', label: 'بانک' },
                { key: 'Code', label: 'کد پیگیری' },
                { key: 'CardNumber', label: 'شماره کارت' },
                { key: 'CreatedAt', label: 'تاریخ' },
            ],
            items: [],
            detailsModal: false,
            detailLoading: false,
            detail: null,
            statusModal: false,
            statusRow: {},
        }
    },
    created() {
        if (this.$store.state.zirehState.listFilter[this.$options.name]) {
            this.filterOptions = this.$store.state.zirehState.listFilter[this.$options.name]
        }
        this.update()
    },
    methods: {
        sortChange(e) {
            if (e.sortBy) {
                this.filterOptions.SortBy = e.sortBy
                this.filterOptions.IsDesc = e.sortDesc
                this.update(1)
            }
        },
        update(pageNumber) {
            this.loading = true
            this.filterOptions.Page = pageNumber || this.filterOptions.Page
            this.$store.commit('zirehState/setListFilter', { key: this.$options.name, value: this.filterOptions })

            this.$http.post(this.$api.order.getlist, this.filterOptions)
                .then(res => {
                    this.totalRows = res.totalRow
                    this.items = res.data
                })
                .finally(() => {
                    this.loading = false
                })
        },
        openDetails(id) {
            this.detailsModal = true
            this.detailLoading = true
            this.detail = null
            this.$http.post(this.$api.order.get, { Id: id })
                .then(res => {
                    this.detail = res.data
                })
                .finally(() => {
                    this.detailLoading = false
                })
        },
        openStatus(row) {
            this.statusRow = { Id: row.Id, Status: row.Status }
            this.statusModal = true
        },
        saveStatus() {
            this.$http.post(this.$api.order.changestatus, {
                Id: this.statusRow.Id,
                Status: this.statusRow.Status,
            })
                .then(() => {
                    this.update()
                })
        },
        time(value) {
            // مقدار TimeSpan به‌صورت "HH:mm:ss" برمی‌گردد
            if (!value) return '—'
            return String(value).substring(0, 5)
        },
        exportExcel() {
            this.downloadFile(this.$api.order.export, this.filterOptions, 'Orders.xls')
        },
        downloadTicket(id) {
            this.downloadFile(this.$api.order.ticket, { Id: id }, 'RH-' + id + '.pdf')
        },
        downloadFile(endpoint, payload, filename) {
            const This = this
            This.processing = true

            const xhr = new XMLHttpRequest()
            xhr.open('POST', this.$http.defaults.baseURL + endpoint)
            xhr.setRequestHeader('Content-Type', 'application/json')
            xhr.setRequestHeader('Authorization', 'Bearer ' + localStorage.getItem('accessToken'))
            xhr.setRequestHeader('Accept-Language', localStorage.getItem('CurrentLocal'))
            xhr.setRequestHeader('Product-Flavor', '11')
            xhr.responseType = 'blob'

            xhr.onreadystatechange = function () {
                if (xhr.readyState !== 4) return

                This.processing = false

                if (xhr.status === 200) {
                    const a = document.createElement('a')
                    a.href = window.URL.createObjectURL(xhr.response)
                    a.download = filename
                    a.style.display = 'none'
                    document.body.appendChild(a)
                    a.click()
                    a.remove()
                } else {
                    // تلاش برای خواندن پیام خطای JSON از داخل blob
                    const reader = new FileReader()
                    reader.onload = () => {
                        let message = 'خطا در دریافت فایل'
                        try {
                            const json = JSON.parse(reader.result)
                            if (json && json.Message) message = json.Message
                        } catch (e) { /* پاسخ متنی نیست */ }
                        This.$bvToast.toast(message, {
                            title: 'خطا در انجام عملیات',
                            variant: 'danger',
                            toaster: 'b-toaster-bottom-right',
                            solid: false,
                        })
                    }
                    reader.readAsText(xhr.response)
                }
            }

            xhr.send(JSON.stringify(payload))
        },
    },
}
</script>
